import { useState } from "react";
import type { Exercise } from "../core/exercises";
import { sortTemplates, type Template } from "../core/templates";
import { ActionSheet } from "./ActionSheet";
import { ConfirmDialog } from "./ConfirmDialog";
import { formatDate, t } from "./texts";

const CARD_LINES = 4;

/** Tab Workout as in Strong: start an empty workout, or tap a template card to start, edit, duplicate or delete it. */
export function WorkoutTab(props: {
  templates: Template[];
  exercises: Exercise[];
  lastDone: Map<string, number>;
  /** the backup reminder's text when it is due */
  backupReminder?: string;
  onSaveBackup(): void;
  onSnoozeBackup(): void;
  /** no id: an empty workout */
  onStart(templateId?: string): void;
  onCreate(): void;
  /** export, import, backup */
  onData(): void;
  onEdit(id: string): void;
  onDuplicate(id: string): void;
  onDelete(id: string): void;
}) {
  const [open, setOpen] = useState<Template>();
  const [confirmDelete, setConfirmDelete] = useState<Template>();
  const names = new Map(props.exercises.map((e) => [e.id, e.name]));
  const lines = (tpl: Template) => tpl.exercises.map((te) => `${te.sets.length} × ${names.get(te.exerciseId) ?? t.template.unknownExercise}`);

  return (
    <>
      <header className="titlebar">
        <h1>{t.workout.title}</h1>
        <button className="secondary" onClick={props.onData}>{t.workout.data}</button>
      </header>
      {props.backupReminder && (
        <div className="notice reminder">
          <p>{props.backupReminder}</p>
          <div className="row">
            <button className="primary" onClick={props.onSaveBackup}>{t.data.saveBackup}</button>
            <button className="secondary" onClick={props.onSnoozeBackup}>{t.data.later}</button>
          </div>
        </div>
      )}
      <button className="primary wide" onClick={() => props.onStart()}>{t.workout.startEmpty}</button>

      <header className="titlebar section-head">
        <h2>{t.workout.templates}</h2>
        <button className="secondary" onClick={props.onCreate}>{t.workout.addTemplate}</button>
      </header>
      {props.templates.length === 0 && <p className="muted-text">{t.workout.noTemplates}</p>}
      <div className="cards">
        {sortTemplates(props.templates).map((tpl) => {
          const all = lines(tpl);
          const last = props.lastDone.get(tpl.id);
          return (
            <button key={tpl.id} className="card" onClick={() => setOpen(tpl)}>
              <span className="name">{tpl.name}</span>
              {all.slice(0, CARD_LINES).map((l, i) => <span key={i} className="sub">{l}</span>)}
              {all.length > CARD_LINES && <span className="sub">{t.workout.moreExercises(all.length - CARD_LINES)}</span>}
              <span className="sub when">{last ? t.workout.lastDone(formatDate(last)) : t.workout.neverDone}</span>
            </button>
          );
        })}
      </div>

      {open && (
        <ActionSheet title={open.name} onClose={() => setOpen(undefined)} actions={[
          { label: t.workout.start, primary: true, onClick: () => { setOpen(undefined); props.onStart(open.id); } },
          { label: t.workout.edit, onClick: () => { setOpen(undefined); props.onEdit(open.id); } },
          { label: t.workout.duplicate, onClick: () => { setOpen(undefined); props.onDuplicate(open.id); } },
          { label: t.workout.delete, danger: true, onClick: () => { setOpen(undefined); setConfirmDelete(open); } },
        ]}>
          {open.notes && <p className="notes muted-text">{open.notes}</p>}
          <ul className="sheet-lines">{lines(open).map((l, i) => <li key={i}>{l}</li>)}</ul>
        </ActionSheet>
      )}
      {confirmDelete && (
        <ConfirmDialog text={t.workout.confirmDelete(confirmDelete.name)} confirm={t.workout.delete} danger
          onCancel={() => setConfirmDelete(undefined)}
          onConfirm={() => { props.onDelete(confirmDelete.id); setConfirmDelete(undefined); }} />
      )}
    </>
  );
}
